import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { loginRequest, b2cPolicyName, metadataUrl, forgetCachedAccount } from "../authConfig";
import { START_PATH, SAMPLE_REPORT, isAnalyticsDestination } from "../utils/landing";
import { peekPostAuthRedirect, savePostAuthRedirect, authReturnFailed, clearAuthReturnFailure } from "../utils/authRedirect";
import { Alert, Box, Button, Link, Typography } from "@mui/material";
import Logo from "../assets/icons/logo_full.png";
import BgImage from "../assets/icons/login-bg.jpg";

const TEAL = "#009688";
const TEAL_DARK = "#00796b";

// What a first-time visitor from an analytics link reads before signing in. Until this, every
// signed-out link — the outreach emails' /analyze included — landed on a bare "Sign in" screen that
// said nothing about the product, that it's free, or what happens next.
const ANALYTICS_POINTS = [
  "Free to start, no card needed",
  "One dataset up to 10MB, or ask it to simulate one",
  "Starter is $299/month when you need more",
];

export default function Login() {
  const isAuth = useIsAuthenticated();
  const { instance } = useMsal();
  const navigate = useNavigate();
  // Read once: where the visitor was headed decides which screen this is.
  const [target] = useState(() => peekPostAuthRedirect());
  // The sign-in return on this page load came back without an account (see index.js).
  const [returnFailed] = useState(() => !!authReturnFailed());
  const analytics = isAnalyticsDestination(target);

  useEffect(() => {
    // Already signed in: nothing here decided a product, so /start does — the agent by default,
    // analytics for accounts that have projects. (This used to be "/", i.e. always analytics.)
    if (isAuth) navigate(START_PATH, { replace: true });
  }, [isAuth, navigate]);

  // `create` opens the tenant's sign-up screen instead of sign-in, for "Start free".
  const doLogin = async ({ create = false } = {}) => {
    const r = await fetch(metadataUrl, { cache: "no-store" });
    if (!r.ok) {
      alert(`Metadata fetch failed: ${r.status}`);
      return;
    }
    // The saved return path expires after a few minutes, and someone reading the panel (or
    // verifying an email on sign-up) can take longer. Save it again at the click so a new
    // analytics account still lands in analytics, not in the agent's default home.
    if (target) savePostAuthRedirect(target);
    clearAuthReturnFailure();
    await forgetCachedAccount(instance);
    await instance.loginRedirect({
      ...loginRequest,
      ...(create ? { prompt: "create" } : {}),
      extraQueryParameters: { p: b2cPolicyName },
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{
        backgroundImage: `url(${BgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        px: 3,
        py: 4,
      }}
    >
      <Box
        component="img"
        src={Logo}
        alt="Company Logo"
        sx={{ height: 64, mb: 3 }}
      />

      {returnFailed && (
        <Alert severity="warning" sx={{ width: "100%", maxWidth: 400, mb: 3 }} data-testid="signin-return-failed">
          Sign-in didn't complete. Please try again. If you're in a private browsing window, a normal window works more reliably.
        </Alert>
      )}

      {analytics ? (
        <>
          <Box sx={{ width: "100%", maxWidth: 400, mb: 3 }} data-testid="analytics-panel">
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: TEAL_DARK, mb: 1 }}>
              Assertion Analytics
            </Typography>
            <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 600, lineHeight: 1.3, color: "#17252f", mb: 1.5, textWrap: "balance" }}>
              Ask your data a question. Get the answer and the code behind it.
            </Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mb: 1.5, display: "grid", gap: 0.75 }}>
              {ANALYTICS_POINTS.map((point) => (
                <Box component="li" key={point} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", fontSize: 15, color: "#33444e" }}>
                  <Box
                    aria-hidden
                    sx={{ flex: "none", width: 6, height: 11, mt: "4px", ml: "3px", mr: "2px", border: `solid ${TEAL}`, borderWidth: "0 2px 2px 0", transform: "rotate(45deg)" }}
                  />
                  {point}
                </Box>
              ))}
            </Box>
            <Link href={SAMPLE_REPORT} target="_blank" rel="noopener" underline="hover" sx={{ fontSize: 15, fontWeight: 600, color: TEAL_DARK }}>
              See a sample report →
            </Link>
          </Box>

          <Button
            onClick={() => doLogin({ create: true })}
            variant="contained"
            size="large"
            sx={{ width: "100%", maxWidth: 320, bgcolor: TEAL, "&:hover": { bgcolor: TEAL_DARK } }}
          >
            Start free
          </Button>
          <Typography sx={{ mt: 1.5, fontSize: 14, color: "#4a5a64" }}>
            Already have an account?{" "}
            <Link component="button" type="button" onClick={() => doLogin()} underline="hover" sx={{ fontSize: 14, fontWeight: 600, color: TEAL_DARK, verticalAlign: "baseline" }}>
              Sign in
            </Link>
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h6" color="black" gutterBottom>
            Sign in or create an account to continue
          </Typography>

          <Button
            onClick={() => doLogin()}
            variant="contained"
            size="large"
            sx={{
              mt: 3,
              width: "100%",
              maxWidth: 320,
              bgcolor: TEAL,
              "&:hover": { bgcolor: TEAL_DARK },
            }}
          >
            Sign in
          </Button>
        </>
      )}
    </Box>
  );
}
